import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that wraps the main Bunnings PDP price.
 * The price is rendered as static Liquid (`bunnings-price-display`), so it does not
 * update on its own when a variant changes. This component listens for variant update
 * events and morphs the freshly fetched price markup into place, mirroring the
 * `product-sku-component` pattern.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} priceContainer - The element wrapping the price markup.
 *
 * @extends {Component<Refs>}
 */
class BunningsProductPriceComponent extends Component {
  requiredRefs = ['priceContainer'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price from the fetched product HTML.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newComponent =
      event.detail.data.html.querySelector(`bunnings-product-price-component[data-product-id="${this.dataset.productId}"]`) ??
      event.detail.data.html.querySelector('bunnings-product-price-component');
    if (!newComponent) return;

    const newPrice = newComponent.querySelector('[ref="priceContainer"]');
    const currentPrice = this.refs.priceContainer;
    if (!newPrice || !currentPrice) return;

    morph(currentPrice, newPrice, { childrenOnly: true });
  };
}

if (!customElements.get('bunnings-product-price-component')) {
  customElements.define('bunnings-product-price-component', BunningsProductPriceComponent);
}
